import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

 export type LeadEmailType = "first_contact" | "followup" | "post_call" | "post_call_failed" | "post_call_success" | "custom";

interface SendEmailParams {
  leadId: string;
  emailType: LeadEmailType;
  customSubject?: string;
  customBody?: string;
  zoomLink?: string;
  meetingDateTime?: string;
  /** Identité de l'expéditeur ; si omis, auto-détectée côté serveur via le JWT. */
  senderKey?: "maxime" | "gueorgui";
}

interface SendEmailResult {
  success: boolean;
  emailId?: string;
  message?: string;
  error?: string;
}

export function useSendLeadEmail() {
  const [sending, setSending] = useState(false);

  const sendEmail = async (params: SendEmailParams): Promise<SendEmailResult> => {
    setSending(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("send-lead-email", {
        body: params,
      });

      if (error) {
        throw new Error(error.message || "Erreur lors de l'envoi");
      }

      if (!data.success) {
        throw new Error(data.error || "Erreur inconnue");
      }

      toast({
        title: "Email envoyé ✉️",
        description: data.message || `Email envoyé avec succès`,
      });

      return {
        success: true,
        emailId: data.emailId,
        message: data.message,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Erreur inconnue";
      
      toast({
        title: "Erreur d'envoi",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setSending(false);
    }
  };

  const getEmailTypeLabel = (type: LeadEmailType): string => {
    const labels: Record<LeadEmailType, string> = {
      first_contact: "Premier contact",
      followup: "Relance",
      post_call: "Post-appel",
      post_call_failed: "Post-appel (échec)",
      post_call_success: "Post-appel (succès)",
      custom: "Email libre",
    };
    return labels[type];
  };

  return {
    sendEmail,
    sending,
    getEmailTypeLabel,
  };
}
